import { useState, useEffect } from 'react'
import axios from 'axios'

const ROWS_PER_PAGE = 100

const AnalyticsDashboard = () => {
  const [data, setData] = useState([])
  const [sortConfig, setSortConfig] = useState({
    key: 'test_code',
    direction: 'asc',
  })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    gettests()
  }, [])

  const gettests = async () => {
    setLoading(true)
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}analytics`
    )
    if (response.status === 200) {
      setData(response.data)
    }
    setLoading(false)
  }

  const sortedData = [...data].sort((a, b) => {
    if (sortConfig.key === 'test_code') {
      // Extract numeric part for sorting
      const numA = parseInt((a.test_code || '').replace(/\D/g, ''), 10)
      const numB = parseInt((b.test_code || '').replace(/\D/g, ''), 10)
      if (isNaN(numA) || isNaN(numB)) return 0
      return sortConfig.direction === 'asc' ? numA - numB : numB - numA
    }
    if (sortConfig.key) {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
    }
    return 0
  })

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE)
  const indexOfLastEntry = page * ROWS_PER_PAGE
  const indexOfFirstEntry = indexOfLastEntry - ROWS_PER_PAGE
  const paginatedData = sortedData.slice(indexOfFirstEntry, indexOfLastEntry)

  const requestSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  // Skeleton loader row
  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 5 }).map((_, i) => (
        <td
          key={i}
          className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]"
        >
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  )

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0] mt-6">
      <div className="justify-between items-center border-b border-[#F0F0F0] flex w-full font-medium leading-[1.2] flex-wrap bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg text-[#030303] font-bold">Statistics</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neutral-50">
            <tr>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0] cursor-pointer"
                onClick={() => requestSort('test_code')}
              >
                Test Number
                {sortConfig.key === 'test_code' &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-60 px-5 py-3.5 border-[#F0F0F0] cursor-pointer"
                onClick={() => requestSort('completions')}
              >
                Number of Completions
                {sortConfig.key === 'completions' &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-60 px-5 py-3.5 border-[#F0F0F0] cursor-pointer"
                onClick={() => requestSort('averageMathScore')}
              >
                Average Math Score
                {sortConfig.key === 'averageMathScore' &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-60 px-5 py-3.5 border-[#F0F0F0] cursor-pointer"
                onClick={() => requestSort('averageRWScore')}
              >
                Average Reading & Writing Score
                {sortConfig.key === 'averageRWScore' &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-60 px-5 py-3.5 border-[#F0F0F0] cursor-pointer"
                onClick={() => requestSort('averageTotalScore')}
              >
                Average Total Score
                {sortConfig.key === 'averageTotalScore' &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <tr key={index} className="border border-[#F0F0F0]">
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0] font-semibold">
                    {row.test_code}
                  </td>
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    {row.completions}
                  </td>
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    {Math.round(row.averageMathScore)}
                  </td>
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    {Math.round(row.averageRWScore)}
                  </td>
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    {Math.round(row.averageTotalScore)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-8 text-[#535862]">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination Controls */}
        {!loading && paginatedData.length > 0 && (
          <div className="flex justify-between items-center mt-4 px-4">
            <div className="text-sm text-[#535862]">
              Showing {sortedData.length === 0 ? 0 : indexOfFirstEntry + 1} to{' '}
              {Math.min(indexOfLastEntry, sortedData.length)} of{' '}
              {sortedData.length} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx + 1}
                  onClick={() => setPage(idx + 1)}
                  className={`px-3 py-1 border border-[#F0F0F0] rounded-md ${
                    page === idx + 1 ? 'bg-[#DE3C7D] text-white' : ''
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .animate-pulse {
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </section>
  )
}

export default AnalyticsDashboard
